import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Box, Typography, TextField, Button, Stack, Paper, FormControl, InputLabel,
    Select, MenuItem, Alert
} from '@mui/material';
import { setMainHeading } from '../../../../redux/modules/mainHeading/slice';
import { getAllQCMaterialsRequest } from '../../../../redux/modules/laboratory/labQC/slice';
import { enterQCResultRequest, resetEnterQCResult } from '../../../../redux/modules/laboratory/labQC/slice';
import { getAllLabAnalyzerRequest } from '../../../../redux/modules/laboratory/labAnalyzer/slice';

export default function LabQCEntry() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const basePath = location.pathname.split('/laboratory')[0];

    const { getAllMaterialsRes, enterResultRes } = useSelector(state => state.labQC);
    const { getAllRes: analyzerRes } = useSelector(state => state.labAnalyzer);

    const [form, setForm] = useState({
        qcMaterialId: '', analyzerId: '', testId: '', value: '', unit: ''
    });
    const [selectedMaterial, setSelectedMaterial] = useState(null);
    const [lastResult, setLastResult] = useState(null);

    useEffect(() => {
        dispatch(setMainHeading("Enter QC Result"));
        dispatch(getAllQCMaterialsRequest());
        dispatch(getAllLabAnalyzerRequest());
        return () => { dispatch(resetEnterQCResult()); };
    }, [dispatch]);

    useEffect(() => {
        if (enterResultRes?.success) {
            setLastResult(enterResultRes);
            setForm(prev => ({ ...prev, value: '' }));
        }
    }, [enterResultRes]);

    const handleMaterialChange = (materialId) => {
        const material = getAllMaterialsRes?.data?.find(m => m._id === materialId);
        setSelectedMaterial(material);
        setForm(prev => ({
            ...prev,
            qcMaterialId: materialId,
            analyzerId: material?.analyzerId?._id || material?.analyzerId || '',
            testId: '',
            unit: ''
        }));
    };

    const handleTestChange = (testId) => {
        const target = selectedMaterial?.targetValues?.find(t => (t.testId?._id || t.testId) === testId);
        setForm(prev => ({
            ...prev,
            testId,
            unit: target?.unit || target?.testId?.unit || ''
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        dispatch(resetEnterQCResult());
        setLastResult(null);
        dispatch(enterQCResultRequest({
            qcMaterialId: form.qcMaterialId,
            analyzerId: form.analyzerId || undefined,
            testId: form.testId,
            value: parseFloat(form.value),
            unit: form.unit,
        }));
    };

    const availableTests = selectedMaterial?.targetValues?.map(tv => ({
        _id: tv.testId?._id || tv.testId,
        label: tv.testId?.name ? `${tv.testId.testCode} - ${tv.testId.name}` : tv.testId?.testCode || 'Unknown',
        targetMean: tv.targetMean,
        targetSD: tv.targetSD,
        unit: tv.unit || tv.testId?.unit || '',
    })) || [];

    const selectedTest = availableTests.find(t => t._id === form.testId);

    return (
        <Box sx={{ maxWidth: 600, mt: 4 }}>
            {lastResult && (
                <Alert
                    severity={lastResult.data?.hasViolation ? 'error' : 'success'}
                    sx={{ mb: 3 }}
                    onClose={() => setLastResult(null)}
                >
                    <Typography variant="subtitle2">{lastResult.message}</Typography>
                    {lastResult.data?.zScore !== undefined && (
                        <Typography variant="body2">z-Score: {lastResult.data.zScore.toFixed(2)}</Typography>
                    )}
                    {lastResult.data?.westgardViolations?.length > 0 && (
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                            Violations: {lastResult.data.westgardViolations.map(v => v.rule.replace('_', '-')).join(', ')}
                        </Typography>
                    )}
                </Alert>
            )}

            <Paper sx={{ p: 3, borderRadius: 3 }} component="form" onSubmit={handleSubmit}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>QC Result Entry</Typography>

                <Stack spacing={2.5}>
                    <FormControl size="small" fullWidth required>
                        <InputLabel>QC Material</InputLabel>
                        <Select
                            value={form.qcMaterialId}
                            onChange={(e) => handleMaterialChange(e.target.value)}
                            label="QC Material"
                        >
                            {getAllMaterialsRes?.data?.map(m => (
                                <MenuItem key={m._id} value={m._id}>
                                    {m.name} - {m.level} (Lot: {m.lotNumber})
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size="small" fullWidth>
                        <InputLabel>Analyzer</InputLabel>
                        <Select
                            value={form.analyzerId}
                            onChange={(e) => setForm(prev => ({ ...prev, analyzerId: e.target.value }))}
                            label="Analyzer"
                        >
                            <MenuItem value="">None</MenuItem>
                            {analyzerRes?.data?.map(a => (
                                <MenuItem key={a._id} value={a._id}>{a.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size="small" fullWidth required>
                        <InputLabel>Test</InputLabel>
                        <Select
                            value={form.testId}
                            onChange={(e) => handleTestChange(e.target.value)}
                            label="Test"
                            disabled={!selectedMaterial}
                        >
                            {availableTests.map(t => (
                                <MenuItem key={t._id} value={t._id}>
                                    {t.label} (Mean: {t.targetMean}, SD: {t.targetSD})
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {selectedTest && (
                        <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                Target: {selectedTest.targetMean} {selectedTest.unit} | SD: {selectedTest.targetSD}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Acceptable: {(selectedTest.targetMean - 2 * selectedTest.targetSD).toFixed(1)} - {(selectedTest.targetMean + 2 * selectedTest.targetSD).toFixed(1)} {selectedTest.unit}
                            </Typography>
                        </Box>
                    )}

                    <Stack direction="row" spacing={2}>
                        <TextField
                            label="Value"
                            type="number"
                            value={form.value}
                            onChange={(e) => setForm(prev => ({ ...prev, value: e.target.value }))}
                            required
                            fullWidth
                            size="small"
                            inputProps={{ step: 'any' }}
                        />
                        <TextField
                            label="Unit"
                            value={form.unit}
                            onChange={(e) => setForm(prev => ({ ...prev, unit: e.target.value }))}
                            fullWidth
                            size="small"
                            sx={{ maxWidth: 150 }}
                        />
                    </Stack>
                </Stack>

                <Stack direction="row" spacing={2} justifyContent="flex-end" mt={3}>
                    <Button variant="outlined" onClick={() => navigate(`${basePath}/laboratory/qc`)}>Back</Button>
                    <Button variant="contained" type="submit" disabled={!form.qcMaterialId || !form.testId || !form.value}>
                        Submit QC Result
                    </Button>
                </Stack>
            </Paper>
        </Box>
    );
}
